import React, { useState, useEffect, useMemo } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import SolicitudesService from "../../services/SolicitudesService";
import SucursalesService from "../../services/SucursalesService";
import DoctoresService from "../../services/DoctoresService";
import EstudiosService from "../../services/EstudiosService";
import SolicitudesEstudiosService from "../../services/SolicitudesEstudiosService";
import ReembolsoService from "../../services/ReembolsoService";
import CajaService from "../../services/CajaService";

import logoLaboratorio from "../../img/logo-laboratorio.jpg";
import Icon from "@mdi/react";
import { mdiFilePdfBox, mdiFileExcel } from "@mdi/js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { ejecutarConCargandoPDF } from "../../utils/pdfLoadingUtils";

import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  Paper,
  Typography,
  Stack,
  Box,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  OutlinedInput,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const SolicitudesGeneradas = () => {
  const getFechaHoy = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const usuarioString = sessionStorage.getItem("usuario");
  const currentU = usuarioString ? JSON.parse(usuarioString) : {};

  const [solicitudes, setSolicitudes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [doctores, setDoctores] = useState([]);
  const [catalogEstudiosMap, setCatalogEstudiosMap] = useState({});
  const [reembolsosList, setReembolsosList] = useState([]);
  const [cajaMap, setCajaMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Filtros principales
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState("");
  const [doctorSeleccionado, setDoctorSeleccionado] = useState("Todos");
  const [busquedaPaciente, setBusquedaPaciente] = useState("");
  const [fechaDe, setFechaDe] = useState(getFechaHoy());
  const [fechaA, setFechaA] = useState(getFechaHoy());

  // Checkboxes Tipo de Solicitudes (los 3 activados por defecto)
  const [chkCanceladas, setChkCanceladas] = useState(true);
  const [chkPagadas, setChkPagadas] = useState(true);
  const [chkPorPagar, setChkPorPagar] = useState(true);

  // Checkboxes Adicionales
  const [chkPorDoctor, setChkPorDoctor] = useState(false);
  const [chkPorEmpleado, setChkPorEmpleado] = useState(false);
  const [chkDetalleEstudios, setChkDetalleEstudios] = useState(false);
  const [chkConEmpresa, setChkConEmpresa] = useState(false);
  const [chkPorConvenio, setChkPorConvenio] = useState(false);
  const [chkDesgloseGanancias, setChkDesgloseGanancias] = useState(true); // Seleccionado por defecto

  // Paginación por día
  const [paginaDia, setPaginaDia] = useState(1);

  useEffect(() => {
    document.title = "Solicitudes Generadas";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resSol, resSuc, resDoc, resCatEst, resCaja] = await Promise.all([
        SolicitudesService.getSolicitudes().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
        DoctoresService.getDoctores().catch(() => ({ data: [] })),
        EstudiosService.getEstudios().catch(() => ({ data: [] })),
        CajaService.getCaja().catch(() => ({ data: [] })),
      ]);

      const listaSol = Array.isArray(resSol?.data?.solicitudes)
        ? resSol.data.solicitudes
        : Array.isArray(resSol?.data)
        ? resSol.data
        : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
        ? resSuc.data
        : [];

      const listaDoc = Array.isArray(resDoc?.data?.doctores)
        ? resDoc.data.doctores
        : Array.isArray(resDoc?.data)
        ? resDoc.data
        : [];

      const listaCatEst = Array.isArray(resCatEst?.data?.estudios)
        ? resCatEst.data.estudios
        : Array.isArray(resCatEst?.data)
        ? resCatEst.data
        : [];

      const listaCaja = Array.isArray(resCaja?.data?.caja)
        ? resCaja.data.caja
        : Array.isArray(resCaja?.caja)
        ? resCaja.caja
        : Array.isArray(resCaja?.data)
        ? resCaja.data
        : [];

      // Mapear catálogo de estudios por idEstudio para obtener código real del estudio
      const catMap = {};
      listaCatEst.forEach((est) => {
        if (est && (est.idEstudio || est.id)) {
          catMap[est.idEstudio || est.id] = est;
        }
      });
      setCatalogEstudiosMap(catMap);

      // Mapear caja por idSolicitud
      const cMap = {};
      listaCaja.forEach((c) => {
        if (c && c.idSolicitud) {
          cMap[c.idSolicitud] = c;
        }
      });
      setCajaMap(cMap);

      // Cargar reembolsos
      const rawReembolsos = [];
      try {
        const resGeneral = await ReembolsoService.getReembolsos();
        const payload = resGeneral?.data?.reembolsos ?? resGeneral?.data;
        if (Array.isArray(payload)) rawReembolsos.push(...payload);
      } catch (e) {}

      // Cargar estudios para cada solicitud
      const solicitudesConEstudios = await Promise.all(
        listaSol.map(async (sol) => {
          try {
            const resEst = await SolicitudesEstudiosService.getEstudiosPorSolicitud(sol.idSolicitud);
            const estudiosArr = Array.isArray(resEst?.estudios)
              ? resEst.estudios
              : Array.isArray(resEst?.data)
              ? resEst.data
              : Array.isArray(resEst)
              ? resEst
              : [];
            return { ...sol, estudios: estudiosArr };
          } catch (e) {
            return { ...sol, estudios: [] };
          }
        })
      );

      setSolicitudes(solicitudesConEstudios);
      setSucursales(listaSuc);
      setDoctores(listaDoc);
      setReembolsosList(rawReembolsos);

      // Determinar la sucursal del usuario actual de forma predeterminada
      const uSucId = currentU.idSucursal || currentU.id_sucursal || currentU.sucursalId;
      const sucValida = listaSuc.find((s) => String(s.idSucursal || s.id) === String(uSucId));
      if (sucValida) {
        setSucursalSeleccionada(String(sucValida.idSucursal || sucValida.id));
      } else if (listaSuc.length > 0) {
        setSucursalSeleccionada(String(listaSuc[0].idSucursal || listaSuc[0].id));
      }
    } catch (error) {
      console.error("Error al cargar datos iniciales:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudieron obtener los datos para el reporte.",
        icon: "error",
        confirmButtonColor: "#006787",
      });
    } finally {
      setLoading(false);
    }
  };

  const parseFechaDate = (fechaRaw) => {
    if (!fechaRaw) return null;
    if (fechaRaw instanceof Date) {
      return isNaN(fechaRaw.getTime())
        ? null
        : new Date(fechaRaw.getFullYear(), fechaRaw.getMonth(), fechaRaw.getDate());
    }
    const str = String(fechaRaw).trim();
    if (!str) return null;

    const datePart = str.split(" ")[0].split("T")[0];

    if (datePart.includes("/")) {
      const parts = datePart.split("/");
      if (parts.length === 3) {
        const d = Number(parts[0]);
        const m = Number(parts[1]) - 1;
        const y = Number(parts[2]);
        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
          return new Date(y, m, d);
        }
      }
    }

    if (datePart.includes("-")) {
      const parts = datePart.split("-");
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          const y = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const d = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        } else if (parts[2].length === 4) {
          const d = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const y = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        }
      }
    }
    return null;
  };

  const formatDateToYYYYMMDD = (dt) => {
    if (!dt) return "";
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  // Clasificación precisa de Estatus de Solicitud
  const isCancelada = (sol) => {
    const est = `${sol.estatus ?? ""}`.trim().toUpperCase();
    return est === "C" || est === "CANCELADA" || Boolean(sol.motivoCancelacion && String(sol.motivoCancelacion).trim() !== "");
  };

  const isPagada = (sol) => {
    if (isCancelada(sol)) return false;
    const est = `${sol.estatus ?? ""}`.trim().toUpperCase();
    if (est === "P" || est === "PAGADA" || est === "LIQUIDADA") return true;

    const totalPagar = Number(sol.totalPagar ?? sol.total ?? sol.monto ?? 0);
    const adeudo = Number(sol.adeudo ?? sol.saldoPendiente ?? -1);
    if (adeudo >= 0 && adeudo <= 0.001) return true;

    const pagado = Number(sol.pagado ?? 0);
    const descuento = Number(sol.descuentoTotal ?? sol.descuento ?? 0);
    if (totalPagar > 0 && pagado >= (totalPagar - descuento)) return true;

    return false;
  };

  const isPorPagar = (sol) => {
    if (isCancelada(sol)) return false;
    if (isPagada(sol)) return false;
    return true;
  };

  // Helper robusto para obtener el Nombre del Doctor de una solicitud
  const getDoctorNombre = (sol) => {
    if (!sol) return "A QUIEN CORRESPONDA";

    // 1. Array de IDs de doctores (retornado por backend en idDoctores o idDoctoresN)
    const idsArr = Array.isArray(sol.idDoctores)
      ? sol.idDoctores
      : Array.isArray(sol.idDoctoresN)
      ? sol.idDoctoresN
      : [];

    if (idsArr.length > 0) {
      const nombresDoc = idsArr
        .map((id) => {
          const found = doctores.find((d) => String(d.idDoctor || d.id) === String(id));
          if (found) {
            return `${found.nombre || ""} ${found.apPaterno || ""} ${found.apMaterno || ""}`.trim();
          }
          return null;
        })
        .filter(Boolean);

      if (nombresDoc.length > 0) {
        return nombresDoc.join(", ");
      }
    }

    // 2. Objeto doctor anidado (si viniera por payload)
    if (sol.doctor && typeof sol.doctor === "object") {
      const nom = `${sol.doctor.nombre || ""} ${sol.doctor.apPaterno || ""} ${sol.doctor.apMaterno || ""}`.trim();
      if (nom) return nom;
    }

    // 3. Coincidencia por IDs directos
    const docIds = [
      sol.idDoctor,
      sol.idDoctor1,
      sol.idDoctor2,
      sol.idDoctor3,
      sol.id_doctor,
      sol.doctorId,
      sol.idDoctorRemitido,
      sol.doctor?.idDoctor,
      sol.doctor?.id,
    ];

    for (const dId of docIds) {
      if (dId !== undefined && dId !== null && String(dId) !== "" && String(dId) !== "0") {
        const found = doctores.find((d) => String(d.idDoctor || d.id) === String(dId));
        if (found) {
          const nom = `${found.nombre || ""} ${found.apPaterno || ""} ${found.apMaterno || ""}`.trim();
          if (nom) return nom;
        }
      }
    }

    // 4. Campos directos de texto en la solicitud
    const nameFields = [
      [sol.nombreDoctor, sol.apPaternoDoctor, sol.apMaternoDoctor].filter(Boolean).join(" ").trim(),
      sol.nombreDoctor,
      sol.doctorNombre,
      sol.doctorRemitido,
      sol.nombreDoctorRemitido,
      typeof sol.doctor === "string" ? sol.doctor : null,
      typeof sol.doctor1 === "string" ? sol.doctor1 : null,
    ];

    for (const n of nameFields) {
      if (n && typeof n === "string" && n.trim() !== "") {
        return n.trim();
      }
    }

    return "A QUIEN CORRESPONDA";
  };

  // Helper para comparar doctor seleccionado con solicitud
  const coincideDoctor = (sol, doctorId) => {
    if (doctorId === "Todos" || !doctorId) return true;
    const targetDoc = doctores.find((d) => String(d.idDoctor || d.id) === String(doctorId));
    if (!targetDoc) return true;

    const targetNom = `${targetDoc.nombre || ""} ${targetDoc.apPaterno || ""} ${targetDoc.apMaterno || ""}`.trim().toLowerCase();
    const docNomSol = getDoctorNombre(sol).toLowerCase();

    if (docNomSol.includes(targetNom) || targetNom.includes(docNomSol)) return true;

    const dIdStr = String(doctorId);
    const idsArr = Array.isArray(sol.idDoctores) ? sol.idDoctores : Array.isArray(sol.idDoctoresN) ? sol.idDoctoresN : [];
    if (idsArr.some((id) => String(id) === dIdStr)) return true;

    const directIds = [sol.idDoctor, sol.idDoctor1, sol.idDoctor2, sol.idDoctor3, sol.id_doctor, sol.doctorId, sol.idDoctorRemitido];
    return directIds.some((id) => id !== undefined && id !== null && String(id) === dIdStr);
  };

  // Desglose financiero por solicitud
  const getDesgloseSolicitud = (sol) => {
    const cajaObj = cajaMap[sol.idSolicitud] || {};

    const totalNetoOriginal = Number(cajaObj.totalPagar ?? cajaObj.total ?? sol.totalPagar ?? sol.total ?? sol.monto ?? 0);
    const descuento = Number(cajaObj.descuento ?? sol.descuentoTotal ?? sol.descuento ?? sol.descuentoMonto ?? 0);
    const iva = Number(cajaObj.iva ?? sol.iva ?? sol.montoIva ?? 0);

    // Total sin descuento = Subtotal de la caja (precio bruto de los estudios sin descuento ni IVA)
    const totalSinDescuento = Number(
      cajaObj.subtotal ??
      sol.subtotal ??
      sol.subtotalCaja ??
      sol.subtotalSolicitud ??
      (totalNetoOriginal + descuento - iva)
    );

    // Total neto = Total sin descuento - Descuento + IVA
    const totalNeto = totalSinDescuento - descuento + iva;

    const pagado = Number(cajaObj.pagado ?? sol.pagado ?? sol.aCuenta ?? 0);

    let adeudo = 0;
    if (cajaObj.adeudo !== undefined && cajaObj.adeudo !== null && cajaObj.adeudo !== "") {
      adeudo = Number(cajaObj.adeudo);
    } else if (sol.saldoPendiente !== undefined && sol.saldoPendiente !== null && sol.saldoPendiente !== "") {
      adeudo = Number(sol.saldoPendiente);
    } else if (sol.adeudo !== undefined && sol.adeudo !== null && sol.adeudo !== "") {
      adeudo = Number(sol.adeudo);
    } else {
      adeudo = Math.max(0, totalNeto - pagado);
    }

    let ef = 0,
      tj = 0,
      ch = 0,
      tr = 0;

    const forma = `${cajaObj.formaPago || sol.formaPago || sol.metodoPago || sol.tipoPago || ""}`.toLowerCase();
    if (forma.includes("tarjeta")) {
      tj = pagado;
    } else if (forma.includes("cheque")) {
      ch = pagado;
    } else if (forma.includes("transferencia") || forma.includes("transf")) {
      tr = pagado;
    } else {
      ef = pagado;
    }

    // Reembolsos asociados
    const reemSol = reembolsosList.filter((r) => {
      const idR = String(r.idSolicitud || r.codigo || "");
      const idS = String(sol.idSolicitud || sol.codigo || "");
      return idR && idR === idS;
    });
    const montoReembolso = reemSol.reduce((acc, r) => acc + Number(r.monto || r.cantidad || 0), 0);

    let estatusPago = "PENDIENTE";
    if (isCancelada(sol)) {
      estatusPago = "CANCELADO";
    } else if (isPagada(sol) || adeudo <= 0.001) {
      estatusPago = "PAGADO";
      adeudo = 0;
    }

    const pacienteNombre = [sol.nombrePaciente, sol.apPaternoPaciente, sol.apMaternoPaciente]
      .filter(Boolean)
      .join(" ")
      .trim() || sol.paciente || "Paciente";

    const doctorNombre = getDoctorNombre(sol);

    const empleadoNombre = [sol.nombreInserto, sol.apPaternoInserto, sol.apMaternoInserto]
      .filter(Boolean)
      .join(" ")
      .trim() || sol.empleado || sol.usuarioCobro || "Sistema";

    const convenioNombre = sol.nombreConvenio || sol.convenio || "Sin Empresa / Convenio";

    // Obtención de estudios principales (filtrando estudiosInt o hijos, usando código del catálogo como en ReporteDescuentos)
    const estudiosArr = (sol.estudios || [])
      .filter((est) => !est.idEstudioPadre && !est.esEstudioInt)
      .map((est) => {
        const catInfo = catalogEstudiosMap[est.idEstudio] || catalogEstudiosMap[est.id] || {};
        const imp = Number(est.costo || est.precio || est.importe || catInfo.precio || catInfo.costo || 0);
        const desc = Number(est.descuento || 0);
        const codEst = est.codigo || est.codigoEstudio || catInfo.codigo || catInfo.codigoEstudio || catInfo.clave || catInfo.claveEstudio || `EST-${est.idEstudio}`;

        return {
          idEstudio: est.idEstudio,
          codigo: codEst,
          estudio: est.nombreEstudio || est.nombre || catInfo.nombre || `Estudio #${est.idEstudio}`,
          importe: imp,
          descuento: desc,
          totalNeto: imp - desc > 0 ? imp - desc : 0,
        };
      });

    return {
      codigo: sol.codigo || sol.codigoSolicitud || String(sol.idSolicitud),
      paciente: pacienteNombre,
      doctor: doctorNombre,
      empleado: empleadoNombre,
      convenio: convenioNombre,
      estatusPago,
      efectivo: ef,
      tarjeta: tj,
      cheque: ch,
      transferencia: tr,
      reembolsos: montoReembolso,
      totalPagado: pagado,
      totalPorPagar: adeudo,
      totalSinDescuento: totalSinDescuento,
      descuento: descuento,
      iva: iva,
      totalNeto: totalNeto,
      motivoDescuento: sol.motivoDescuento || "",
      estudios: estudiosArr,
    };
  };

  // --- FILTRADO DE SOLICITUDES EN EL RANGO ---
  const solicitudesFiltradas = useMemo(() => {
    const start = parseFechaDate(fechaDe <= fechaA ? fechaDe : fechaA);
    const end = parseFechaDate(fechaDe <= fechaA ? fechaA : fechaDe);

    if (!start || !end) return [];

    return solicitudes.filter((sol) => {
      // 1. Filtro de sucursal obligatoria
      const sId = sol.idSucursal || sol.id_sucursal;
      if (sucursalSeleccionada && String(sId) !== String(sucursalSeleccionada)) {
        return false;
      }

      // 2. Filtro de fecha
      const dtSol = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.createdAt);
      if (!dtSol) return false;
      if (dtSol.getTime() < start.getTime() || dtSol.getTime() > end.getTime()) return false;

      // 3. Filtro de Doctor
      if (!coincideDoctor(sol, doctorSeleccionado)) {
        return false;
      }

      // 4. Filtro de Paciente (coincidencias de texto)
      if (busquedaPaciente.trim() !== "") {
        const pacNom = [sol.nombrePaciente, sol.apPaternoPaciente, sol.apMaternoPaciente, sol.paciente]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!pacNom.includes(busquedaPaciente.trim().toLowerCase())) {
          return false;
        }
      }

      // 5. Filtro de Tipo de Solicitudes (Checkboxes)
      const cancel = isCancelada(sol);
      const pagada = isPagada(sol);
      const porPagar = isPorPagar(sol);

      let pasaTipo = false;
      if (chkCanceladas && cancel) pasaTipo = true;
      if (chkPagadas && pagada) pasaTipo = true;
      if (chkPorPagar && porPagar) pasaTipo = true;
      if (!pasaTipo) return false;

      // 6. Filtro de Empresa / Convenio obligado
      if (chkConEmpresa) {
        const tieneEmpresa = Boolean(sol.idConvenio || (sol.nombreConvenio && sol.nombreConvenio.trim() !== ""));
        if (!tieneEmpresa) return false;
      }

      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    solicitudes,
    sucursalSeleccionada,
    doctorSeleccionado,
    busquedaPaciente,
    fechaDe,
    fechaA,
    chkCanceladas,
    chkPagadas,
    chkPorPagar,
    chkConEmpresa,
    doctores,
    cajaMap,
  ]);

  // --- DÍAS CON REGISTROS PARA PAGINACIÓN DIARIA ---
  const diasConRegistros = useMemo(() => {
    const datesSet = new Set();
    solicitudesFiltradas.forEach((sol) => {
      const dt = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.createdAt);
      if (dt) {
        datesSet.add(formatDateToYYYYMMDD(dt));
      }
    });
    return Array.from(datesSet).sort();
  }, [solicitudesFiltradas]);

  useEffect(() => {
    setPaginaDia(1);
  }, [fechaDe, fechaA, sucursalSeleccionada, doctorSeleccionado, busquedaPaciente, chkCanceladas, chkPagadas, chkPorPagar, chkConEmpresa, chkPorDoctor, chkPorEmpleado, chkPorConvenio]);

  const diaActivo = diasConRegistros[paginaDia - 1] || (diasConRegistros.length > 0 ? diasConRegistros[0] : fechaDe);

  const paginasVisibles = useMemo(() => {
    const total = diasConRegistros.length;
    if (total <= 10) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    let start = Math.max(1, paginaDia - 4);
    let end = start + 9;
    if (end > total) {
      end = total;
      start = Math.max(1, end - 9);
    }
    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }, [diasConRegistros.length, paginaDia]);

  // Solicitudes del DÍA ACTIVO
  const solicitudesDiaActivo = useMemo(() => {
    if (!diaActivo) return [];
    return solicitudesFiltradas.filter((sol) => {
      const dt = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.createdAt);
      return dt && formatDateToYYYYMMDD(dt) === diaActivo;
    });
  }, [solicitudesFiltradas, diaActivo]);

  // --- AGRUPACIÓN DE SOLICITUDES SEGÚN ADICIONALES ---
  const construirGrupos = (listaSol) => {
    let listaProcesada = [...listaSol];
    if (chkPorConvenio) {
      listaProcesada.sort((a, b) => (a.nombreConvenio || "").localeCompare(b.nombreConvenio || ""));
    }

    if (chkPorDoctor && chkPorEmpleado) {
      const docGroups = {};
      listaProcesada.forEach((sol) => {
        const desg = getDesgloseSolicitud(sol);
        const docKey = desg.doctor;
        if (!docGroups[docKey]) docGroups[docKey] = {};
        const empKey = desg.empleado;
        if (!docGroups[docKey][empKey]) docGroups[docKey][empKey] = [];
        docGroups[docKey][empKey].push(desg);
      });
      return { tipo: "DOCTOR_EMPLEADO", data: docGroups };
    }

    if (chkPorDoctor) {
      const docGroups = {};
      listaProcesada.forEach((sol) => {
        const desg = getDesgloseSolicitud(sol);
        const docKey = desg.doctor;
        if (!docGroups[docKey]) docGroups[docKey] = [];
        docGroups[docKey].push(desg);
      });
      return { tipo: "DOCTOR", data: docGroups };
    }

    if (chkPorEmpleado) {
      const empGroups = {};
      listaProcesada.forEach((sol) => {
        const desg = getDesgloseSolicitud(sol);
        const empKey = desg.empleado;
        if (!empGroups[empKey]) empGroups[empKey] = [];
        empGroups[empKey].push(desg);
      });
      return { tipo: "EMPLEADO", data: empGroups };
    }

    return { tipo: "FLAT", data: listaProcesada.map(getDesgloseSolicitud) };
  };

  const gruposDiaActivo = useMemo(() => {
    return construirGrupos(solicitudesDiaActivo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudesDiaActivo, chkPorDoctor, chkPorEmpleado, chkPorConvenio, doctores, catalogEstudiosMap]);

  // Nombre de la sucursal activa seleccionada
  const nombreSucursalActiva = useMemo(() => {
    const sObj = sucursales.find((s) => String(s.idSucursal || s.id) === String(sucursalSeleccionada));
    return (sObj?.nombre || sObj?.nombreSucursal || "SUCURSAL").toUpperCase();
  }, [sucursales, sucursalSeleccionada]);

  // Totales acumulados para el día activo
  const totalesDiaActivo = useMemo(() => {
    const listaDesglose = solicitudesDiaActivo.map(getDesgloseSolicitud);
    return listaDesglose.reduce(
      (acc, d) => ({
        efectivo: acc.efectivo + d.efectivo,
        tarjeta: acc.tarjeta + d.tarjeta,
        cheque: acc.cheque + d.cheque,
        transferencia: acc.transferencia + d.transferencia,
        reembolsos: acc.reembolsos + d.reembolsos,
        totalPagado: acc.totalPagado + d.totalPagado,
        totalPorPagar: acc.totalPorPagar + d.totalPorPagar,
        totalSinDescuento: acc.totalSinDescuento + d.totalSinDescuento,
        descuento: acc.descuento + d.descuento,
        iva: acc.iva + d.iva,
        totalNeto: acc.totalNeto + d.totalNeto,
      }),
      {
        efectivo: 0,
        tarjeta: 0,
        cheque: 0,
        transferencia: 0,
        reembolsos: 0,
        totalPagado: 0,
        totalPorPagar: 0,
        totalSinDescuento: 0,
        descuento: 0,
        iva: 0,
        totalNeto: 0,
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudesDiaActivo, doctores, catalogEstudiosMap, cajaMap]);

  // --- EXPORTAR A PDF (TODAS LAS SOLICITUDES EN EL RANGO SELECCIONADO) ---
  const handleExportarPDF = async () => {
    if (solicitudesFiltradas.length === 0) {
      Swal.fire({
        title: "Sin Información",
        text: "No existen solicitudes para exportar en el rango seleccionado.",
        icon: "info",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#006787",
      });
      return;
    }

    await ejecutarConCargandoPDF(
      async () => {
        const doc = new jsPDF("landscape", "mm", "a4");
        const startY = 12;

    let sucursalActualObj = sucursales.find((s) => String(s.idSucursal || s.id) === String(sucursalSeleccionada)) || sucursales[0] || {};
    const sucursalNomText = (sucursalActualObj.nombre || "LABORATORIO DE ANÁLISIS CLÍNICOS").toUpperCase();
    const calleText = [sucursalActualObj.calle, sucursalActualObj.numExterior, sucursalActualObj.numInterior].filter(Boolean).join(" ");
    const colCpText = `Col. ${sucursalActualObj.colonia || ""} C.P. ${sucursalActualObj.cPostal || ""} ${sucursalActualObj.ciudad || ""}, ${sucursalActualObj.estado || ""}`;

    // Dibujar Logo
    try {
      doc.addImage(logoLaboratorio, "JPEG", 14, startY, 45, 24);
    } catch (e) {}

    // Recuadro "INFORMES"
    doc.setFillColor(24, 35, 79);
    doc.rect(202, startY, 81, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("INFORMES", 242.5, startY + 5, { align: "center" });

    // Encabezado de la Sucursal en la derecha
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(sucursalNomText, 283, startY + 12, { align: "right" });

    let currY = startY + 16;
    if (calleText.trim()) {
      doc.setFont("helvetica", "normal");
      doc.text(`Dirección: ${calleText}`, 283, currY, { align: "right" });
      currY += 4;
    }
    doc.setFont("helvetica", "normal");
    doc.text(colCpText, 283, currY, { align: "right" });

    // Título y Período
    currY += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(24, 35, 79);
    doc.text("SOLICITUDES GENERADAS", 14, currY);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Período del ${fechaDe} al ${fechaA}`, 14, currY + 5);

    let nextY = currY + 12;

    const listaDesgloseCompleta = solicitudesFiltradas
      .map(getDesgloseSolicitud)
      .filter((sol) => sol.estatusPago !== "CANCELADO");

    let headCols = [];
    if (chkDesgloseGanancias) {
      headCols = [
        "Código",
        "Paciente",
        "Estatus",
        "Efectivo",
        "Tarjeta",
        "Cheque",
        "Transf.",
        "Reembolsos",
        "Total pagado",
        "Por pagar",
        "Total s/desc",
        "Descuento",
        "IVA",
        "Total neto",
      ];
    } else {
      headCols = ["Código", "Paciente"];
    }

    const bodyPDF = [];
    listaDesgloseCompleta.forEach((sol) => {
      if (chkDesgloseGanancias) {
        bodyPDF.push([
          sol.codigo,
          sol.paciente,
          sol.estatusPago,
          `$${sol.efectivo.toFixed(2)}`,
          `$${sol.tarjeta.toFixed(2)}`,
          `$${sol.cheque.toFixed(2)}`,
          `$${sol.transferencia.toFixed(2)}`,
          `$${sol.reembolsos.toFixed(2)}`,
          `$${sol.totalPagado.toFixed(2)}`,
          `$${sol.totalPorPagar.toFixed(2)}`,
          `$${sol.totalSinDescuento.toFixed(2)}`,
          `$${sol.descuento.toFixed(2)}`,
          `$${sol.iva.toFixed(2)}`,
          `$${sol.totalNeto.toFixed(2)}`,
        ]);
      } else {
        bodyPDF.push([sol.codigo, sol.paciente]);
      }

      if (sol.descuento > 0 && sol.motivoDescuento) {
        bodyPDF.push([
          {
            content: `Motivo de descuento: ${sol.motivoDescuento}`,
            colSpan: headCols.length,
            styles: { fontStyle: "italic", textColor: [183, 129, 3] },
          },
        ]);
      }
    });

    if (chkDesgloseGanancias) {
      const totGen = listaDesgloseCompleta.reduce(
        (acc, d) => ({
          efectivo: acc.efectivo + d.efectivo,
          tarjeta: acc.tarjeta + d.tarjeta,
          cheque: acc.cheque + d.cheque,
          transferencia: acc.transferencia + d.transferencia,
          reembolsos: acc.reembolsos + d.reembolsos,
          totalPagado: acc.totalPagado + d.totalPagado,
          totalPorPagar: acc.totalPorPagar + d.totalPorPagar,
          totalSinDescuento: acc.totalSinDescuento + d.totalSinDescuento,
          descuento: acc.descuento + d.descuento,
          iva: acc.iva + d.iva,
          totalNeto: acc.totalNeto + d.totalNeto,
        }),
        { efectivo: 0, tarjeta: 0, cheque: 0, transferencia: 0, reembolsos: 0, totalPagado: 0, totalPorPagar: 0, totalSinDescuento: 0, descuento: 0, iva: 0, totalNeto: 0 }
      );

      bodyPDF.push([
        "TOTAL ACUMULADO",
        "",
        "",
        `$${totGen.efectivo.toFixed(2)}`,
        `$${totGen.tarjeta.toFixed(2)}`,
        `$${totGen.cheque.toFixed(2)}`,
        `$${totGen.transferencia.toFixed(2)}`,
        `$${totGen.reembolsos.toFixed(2)}`,
        `$${totGen.totalPagado.toFixed(2)}`,
        `$${totGen.totalPorPagar.toFixed(2)}`,
        `$${totGen.totalSinDescuento.toFixed(2)}`,
        `$${totGen.descuento.toFixed(2)}`,
        `$${totGen.iva.toFixed(2)}`,
        `$${totGen.totalNeto.toFixed(2)}`,
      ]);
    }

    autoTable(doc, {
      startY: nextY,
      head: [
        [
          {
            content: `SUCURSAL: ${nombreSucursalActiva}`,
            colSpan: headCols.length,
            styles: { halign: "left", fillColor: [24, 35, 79], textColor: [255, 255, 255], fontStyle: "bold" },
          },
        ],
        headCols,
      ],
      body: bodyPDF,
      headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.5 },
      styles: { fontSize: 7, cellPadding: 1.8, halign: "center", valign: "middle" },
      columnStyles: { 0: { halign: "left", fontStyle: "bold" }, 1: { halign: "left" } },
    });

    // Pie de Página
    const pageCount = doc.internal.getNumberOfPages();
    const hoyImpStr = new Date().toLocaleString();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Impreso: ${hoyImpStr}`, 14, 200);
      doc.text(`Página ${i} de ${pageCount}`, 283, 200, { align: "right" });
    }

        doc.save(`Solicitudes_Generadas_${fechaDe}_al_${fechaA}.pdf`);
      },
      "Generando reporte de Solicitudes Generadas en PDF...",
      "Procesando documento"
    );
  };

  // --- EXPORTAR A EXCEL ---
  const handleExportarExcel = () => {
    if (solicitudesFiltradas.length === 0) {
      Swal.fire({
        title: "Sin Información",
        text: "No existen solicitudes para exportar en el rango seleccionado.",
        icon: "info",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#006787",
      });
      return;
    }

    const listaDesgloseCompleta = solicitudesFiltradas
      .map(getDesgloseSolicitud)
      .filter((sol) => sol.estatusPago !== "CANCELADO");

    const rowsExcel = [
      [
        "Sucursal",
        "Código",
        "Paciente",
        "Doctor",
        "Empleado",
        "Convenio",
        "Estatus Pago",
        "Pago Efectivo",
        "Pago Tarjeta",
        "Pago Cheque",
        "Pago Transferencia",
        "Reembolsos",
        "Total Pagado",
        "Total Por Pagar",
        "Total Sin Descuento",
        "Descuento",
        "IVA",
        "Total Neto",
        "Motivo Descuento",
      ],
    ];

    listaDesgloseCompleta.forEach((sol) => {
      rowsExcel.push([
        nombreSucursalActiva,
        sol.codigo,
        sol.paciente,
        sol.doctor,
        sol.empleado,
        sol.convenio,
        sol.estatusPago,
        sol.efectivo,
        sol.tarjeta,
        sol.cheque,
        sol.transferencia,
        sol.reembolsos,
        sol.totalPagado,
        sol.totalPorPagar,
        sol.totalSinDescuento,
        sol.descuento,
        sol.iva,
        sol.totalNeto,
        sol.motivoDescuento || "",
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rowsExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Solicitudes Generadas");

    XLSX.writeFile(workbook, `Reporte_Solicitudes_Generadas_${fechaDe}_al_${fechaA}.xlsx`);
  };

  // Renders auxiliares para las filas de solicitudes
  const renderFilaSolicitud = (sol) => {
    const numCols = chkDesgloseGanancias ? 14 : 2;
    return (
      <React.Fragment key={sol.codigo}>
        <TableRow sx={{ "&:hover": { backgroundColor: "#f1f5f9" } }}>
          <TableCell sx={{ fontWeight: "bold", color: "#006787", px: 1, py: 0.8, fontSize: "0.78rem" }}>{sol.codigo}</TableCell>
          <TableCell sx={{ fontWeight: "bold", color: "#1e293b", px: 1, py: 0.8, fontSize: "0.78rem" }}>{sol.paciente}</TableCell>
          {chkDesgloseGanancias && (
            <>
              <TableCell align="center" sx={{ fontWeight: "bold", px: 1, py: 0.8, fontSize: "0.78rem", color: sol.estatusPago === "PAGADO" ? "#2e7d32" : sol.estatusPago === "CANCELADO" ? "#d32f2f" : "#ed6c02" }}>
                {sol.estatusPago}
              </TableCell>
              <TableCell align="center" sx={{ px: 1, py: 0.8, fontSize: "0.78rem" }}>${sol.efectivo.toFixed(2)}</TableCell>
              <TableCell align="center" sx={{ px: 1, py: 0.8, fontSize: "0.78rem" }}>${sol.tarjeta.toFixed(2)}</TableCell>
              <TableCell align="center" sx={{ px: 1, py: 0.8, fontSize: "0.78rem" }}>${sol.cheque.toFixed(2)}</TableCell>
              <TableCell align="center" sx={{ px: 1, py: 0.8, fontSize: "0.78rem" }}>${sol.transferencia.toFixed(2)}</TableCell>
              <TableCell align="center" sx={{ px: 1, py: 0.8, fontSize: "0.78rem", color: "#d32f2f" }}>${sol.reembolsos.toFixed(2)}</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold", px: 1, py: 0.8, fontSize: "0.78rem" }}>${sol.totalPagado.toFixed(2)}</TableCell>
              <TableCell align="center" sx={{ px: 1, py: 0.8, fontSize: "0.78rem", color: sol.totalPorPagar > 0 ? "#d32f2f" : "inherit" }}>
                ${sol.totalPorPagar.toFixed(2)}
              </TableCell>
              <TableCell align="center" sx={{ px: 1, py: 0.8, fontSize: "0.78rem" }}>${sol.totalSinDescuento.toFixed(2)}</TableCell>
              <TableCell align="center" sx={{ px: 1, py: 0.8, fontSize: "0.78rem", color: "#e65100" }}>${sol.descuento.toFixed(2)}</TableCell>
              <TableCell align="center" sx={{ px: 1, py: 0.8, fontSize: "0.78rem", color: "#1976d2", fontWeight: "bold" }}>${sol.iva.toFixed(2)}</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold", color: "#006787", px: 1, py: 0.8, fontSize: "0.78rem" }}>
                ${sol.totalNeto.toFixed(2)}
              </TableCell>
            </>
          )}
        </TableRow>

        {/* Motivo de descuento si existe */}
        {sol.descuento > 0 && sol.motivoDescuento && (
          <TableRow sx={{ backgroundColor: "#fff8e1" }}>
            <TableCell colSpan={numCols} sx={{ py: 0.6, px: 2 }}>
              <Typography variant="caption" sx={{ color: "#b78103", fontWeight: "bold" }}>
                Motivo de descuento: {sol.motivoDescuento}
              </Typography>
            </TableCell>
          </TableRow>
        )}

        {/* Mostrar detalle de estudios realizados (Idéntico estilo a ReporteDescuentos) */}
        {chkDetalleEstudios && sol.estudios && sol.estudios.length > 0 && (
          <TableRow sx={{ backgroundColor: "#f0f4f8" }}>
            <TableCell colSpan={numCols} sx={{ pl: 4, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#18234f", mb: 1 }}>
                Detalle de estudios realizados
              </Typography>
              <Table size="small" sx={{ width: "98%", backgroundColor: "#ffffff", border: "1px solid #c4d1db" }}>
                <TableHead sx={{ backgroundColor: "#e2e9ef" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>Estudio</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>Importe</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>Descuento</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>Total Neto</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sol.estudios.map((estItem, idx) => (
                    <TableRow key={idx} sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                      <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold", color: "#006787" }}>{estItem.codigo}</TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold", color: "#1e293b" }}>{estItem.estudio}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.75rem" }}>${estItem.importe.toFixed(2)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.75rem", color: "#d32f2f" }}>${estItem.descuento.toFixed(2)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.75rem", fontWeight: "bold", color: "#18234f" }}>${estItem.totalNeto.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  const renderContenidoGrupos = () => {
    const numCols = chkDesgloseGanancias ? 14 : 2;

    if (gruposDiaActivo.tipo === "FLAT") {
      return gruposDiaActivo.data.map(renderFilaSolicitud);
    }

    if (gruposDiaActivo.tipo === "DOCTOR") {
      return Object.entries(gruposDiaActivo.data).map(([docNombre, listaSol]) => (
        <React.Fragment key={docNombre}>
          <TableRow sx={{ backgroundColor: "#e2e8f0" }}>
            <TableCell colSpan={numCols} sx={{ py: 1, px: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#006787" }}>
                DOCTOR: {docNombre.toUpperCase()}
              </Typography>
            </TableCell>
          </TableRow>
          {listaSol.map(renderFilaSolicitud)}
        </React.Fragment>
      ));
    }

    if (gruposDiaActivo.tipo === "EMPLEADO") {
      return Object.entries(gruposDiaActivo.data).map(([empNombre, listaSol]) => (
        <React.Fragment key={empNombre}>
          <TableRow sx={{ backgroundColor: "#e2e8f0" }}>
            <TableCell colSpan={numCols} sx={{ py: 1, px: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#18234f" }}>
                EMPLEADO: {empNombre.toUpperCase()}
              </Typography>
            </TableCell>
          </TableRow>
          {listaSol.map(renderFilaSolicitud)}
        </React.Fragment>
      ));
    }

    if (gruposDiaActivo.tipo === "DOCTOR_EMPLEADO") {
      return Object.entries(gruposDiaActivo.data).map(([docNombre, empMap]) => (
        <React.Fragment key={docNombre}>
          <TableRow sx={{ backgroundColor: "#cbd5e1" }}>
            <TableCell colSpan={numCols} sx={{ py: 1, px: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#006787" }}>
                DOCTOR: {docNombre.toUpperCase()}
              </Typography>
            </TableCell>
          </TableRow>
          {Object.entries(empMap).map(([empNombre, listaSol]) => (
            <React.Fragment key={`${docNombre}_${empNombre}`}>
              <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                <TableCell colSpan={numCols} sx={{ py: 0.8, pl: 4 }}>
                  <Typography variant="body2" sx={{ fontWeight: "bold", color: "#18234f" }}>
                    EMPLEADO: {empNombre.toUpperCase()}
                  </Typography>
                </TableCell>
              </TableRow>
              {listaSol.map(renderFilaSolicitud)}
            </React.Fragment>
          ))}
        </React.Fragment>
      ));
    }

    return null;
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "80vh",
            py: 3,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  REPORTE DE SOLICITUDES GENERADAS
                </Typography>
              </Box>

              {/* Filtros del Reporte */}
              <Box sx={{ width: "100%", mb: 4 }}>
                <Table
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": { padding: "10px 14px" },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}>
                        Filtros del Reporte
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Fila Sucursal y Doctor */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "15%", backgroundColor: "#f8f9fa" }}>
                        Sucursal:
                      </TableCell>
                      <TableCell sx={{ width: "35%" }}>
                        <Select
                          fullWidth
                          size="small"
                          input={<OutlinedInput />}
                          value={sucursalSeleccionada}
                          onChange={(e) => setSucursalSeleccionada(e.target.value)}
                        >
                          {sucursales.map((suc) => (
                            <MenuItem key={suc.idSucursal || suc.id} value={String(suc.idSucursal || suc.id)}>
                              {suc.nombre || suc.nombreSucursal}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>

                      <TableCell sx={{ fontWeight: "bold", width: "15%", backgroundColor: "#f8f9fa" }}>
                        Doctor:
                      </TableCell>
                      <TableCell sx={{ width: "35%" }}>
                        <Select
                          fullWidth
                          size="small"
                          input={<OutlinedInput />}
                          value={doctorSeleccionado}
                          onChange={(e) => setDoctorSeleccionado(e.target.value)}
                        >
                          <MenuItem value="Todos">Todos los doctores</MenuItem>
                          {doctores.map((doc) => {
                            const esVigente = doc.estatus === "A" || doc.vigente === 1 || doc.activo === true || String(doc.estatus).toUpperCase() !== "B";
                            const tag = esVigente ? "(Vigente)" : "(Baja)";
                            const nom = `${doc.nombre || ""} ${doc.apPaterno || ""} ${doc.apMaterno || ""}`.trim();
                            return (
                              <MenuItem key={doc.idDoctor || doc.id} value={String(doc.idDoctor || doc.id)}>
                                {`${nom} ${tag}`}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Paciente */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Paciente:
                      </TableCell>
                      <TableCell colSpan={3}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <TextField
                            size="small"
                            placeholder="Buscar paciente por coincidencia de nombre..."
                            value={busquedaPaciente}
                            onChange={(e) => setBusquedaPaciente(e.target.value)}
                            sx={{ width: "380px" }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setBusquedaPaciente("")}
                            sx={{ backgroundColor: "#006787", "&:hover": { backgroundColor: "#014e65" }, fontWeight: "bold" }}
                          >
                            Mostrar todos
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Fila Tipo de solicitudes */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Tipo de solicitudes:
                      </TableCell>
                      <TableCell colSpan={3}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={chkCanceladas}
                                onChange={(e) => setChkCanceladas(e.target.checked)}
                                size="small"
                                sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                              />
                            }
                            label={<Typography variant="body2">Solicitudes canceladas</Typography>}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={chkPagadas}
                                onChange={(e) => setChkPagadas(e.target.checked)}
                                size="small"
                                sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                              />
                            }
                            label={<Typography variant="body2">Solicitudes pagadas</Typography>}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={chkPorPagar}
                                onChange={(e) => setChkPorPagar(e.target.checked)}
                                size="small"
                                sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                              />
                            }
                            label={<Typography variant="body2">Solicitudes por pagar</Typography>}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Fila Adicionales */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Adicionales:
                      </TableCell>
                      <TableCell colSpan={3}>
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={6} md={4}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={chkPorDoctor}
                                  onChange={(e) => setChkPorDoctor(e.target.checked)}
                                  size="small"
                                  sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                                />
                              }
                              label={<Typography variant="body2">Mostrar solicitudes por doctor</Typography>}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={chkPorEmpleado}
                                  onChange={(e) => setChkPorEmpleado(e.target.checked)}
                                  size="small"
                                  sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                                />
                              }
                              label={<Typography variant="body2">Mostrar solicitudes por empleado</Typography>}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={chkDetalleEstudios}
                                  onChange={(e) => setChkDetalleEstudios(e.target.checked)}
                                  size="small"
                                  sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                                />
                              }
                              label={<Typography variant="body2">Mostrar detalle de estudios realizados</Typography>}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={chkConEmpresa}
                                  onChange={(e) => setChkConEmpresa(e.target.checked)}
                                  size="small"
                                  sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                                />
                              }
                              label={<Typography variant="body2">Mostrar solicitudes con empresa</Typography>}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={chkPorConvenio}
                                  onChange={(e) => setChkPorConvenio(e.target.checked)}
                                  size="small"
                                  sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                                />
                              }
                              label={<Typography variant="body2">Ordenar solicitudes por convenio</Typography>}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={chkDesgloseGanancias}
                                  onChange={(e) => setChkDesgloseGanancias(e.target.checked)}
                                  size="small"
                                  sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                                />
                              }
                              label={<Typography variant="body2">Mostrar desglose de ganancias</Typography>}
                            />
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>

                    {/* Fila Rango de Fechas */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Seleccione un rango de fechas:
                      </TableCell>
                      <TableCell colSpan={3}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <TextField
                            label="De:"
                            type="date"
                            size="small"
                            value={fechaDe}
                            onChange={(e) => setFechaDe(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 170 }}
                          />
                          <TextField
                            label="A:"
                            type="date"
                            size="small"
                            value={fechaA}
                            onChange={(e) => setFechaA(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 170 }}
                          />
                          <IconButton
                            onClick={fetchDatosIniciales}
                            sx={{ backgroundColor: "#006787", color: "white", "&:hover": { backgroundColor: "#014e65" } }}
                            title="Actualizar Datos"
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress size={50} sx={{ color: "#006787" }} />
                </Box>
              ) : (
                <Stack spacing={3} sx={{ width: "100%" }}>
                  {/* TABLA PRINCIPAL DEL DÍA ACTIVO */}
                  <Paper elevation={2} sx={{ p: 0, borderRadius: 3, overflow: "hidden", backgroundColor: "#ffffff", width: "100%" }}>
                    <Box sx={{ backgroundColor: "#18234f", color: "white", px: 3, py: 1.5 }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1.05rem" }}>
                        SUCURSAL: {nombreSucursalActiva}
                      </Typography>
                    </Box>

                    <Table size="small" sx={{ width: "100%", tableLayout: "auto", border: "1px solid #e2e8f0" }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#006787" }}>
                          <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>Código</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>Paciente</TableCell>
                          {chkDesgloseGanancias && (
                            <>
                              <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>Estatus pago día de hoy</TableCell>
                              <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>Pago efectivo</TableCell>
                              <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>Pago tarjeta</TableCell>
                              <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>Pago cheque</TableCell>
                              <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>Pago transferencia</TableCell>
                              <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>Reembolsos</TableCell>
                              <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>Total pagado</TableCell>
                              <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>Total por pagar</TableCell>
                              <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>Total sin descuento</TableCell>
                              <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>Descuento</TableCell>
                              <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>IVA</TableCell>
                              <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>Total neto</TableCell>
                            </>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {solicitudesDiaActivo.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={chkDesgloseGanancias ? 14 : 2} align="center" sx={{ py: 3, color: "#64748b" }}>
                              No existen solicitudes generadas para los filtros seleccionados en el día {diaActivo}.
                            </TableCell>
                          </TableRow>
                        ) : (
                          renderContenidoGrupos()
                        )}

                        {/* Fila Total Acumulado para Desglose de Ganancias */}
                        {solicitudesDiaActivo.length > 0 && chkDesgloseGanancias && (
                          <TableRow sx={{ backgroundColor: "#f8fafc", borderTop: "2px solid #006787" }}>
                            <TableCell colSpan={3} sx={{ fontWeight: "bold", color: "#006787", fontSize: "0.85rem", px: 0.8, py: 1 }}>
                              TOTAL ACUMULADO DÍA
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>${totalesDiaActivo.efectivo.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>${totalesDiaActivo.tarjeta.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>${totalesDiaActivo.cheque.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>${totalesDiaActivo.transferencia.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1, color: "#d32f2f" }}>${totalesDiaActivo.reembolsos.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1, color: "#006787" }}>${totalesDiaActivo.totalPagado.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1, color: "#d32f2f" }}>${totalesDiaActivo.totalPorPagar.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1 }}>${totalesDiaActivo.totalSinDescuento.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1, color: "#e65100" }}>${totalesDiaActivo.descuento.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem", px: 0.8, py: 1, color: "#1976d2" }}>${totalesDiaActivo.iva.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#006787", fontSize: "0.85rem", px: 0.8, py: 1 }}>
                              ${totalesDiaActivo.totalNeto.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Paper>

                  {/* --- PAGINACIÓN POR DÍA (Estilo ReporteCaja) --- */}
                  {diasConRegistros.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 1,
                        py: 2,
                        mt: 2,
                      }}
                    >
                      <IconButton
                        disabled={paginaDia <= 1}
                        onClick={() => setPaginaDia((prev) => Math.max(1, prev - 1))}
                        sx={{ backgroundColor: "#e2e8f0", "&:hover": { backgroundColor: "#cbd5e1" } }}
                      >
                        <NavigateBeforeIcon />
                      </IconButton>

                      {paginasVisibles.map((numPag) => {
                        const esActivo = numPag === paginaDia;
                        return (
                          <IconButton
                            key={numPag}
                            onClick={() => setPaginaDia(numPag)}
                            sx={{
                              width: 36,
                              height: 36,
                              fontSize: "0.88rem",
                              fontWeight: "bold",
                              backgroundColor: esActivo ? "#006787" : "#f1f5f9",
                              color: esActivo ? "white" : "#334155",
                              "&:hover": {
                                backgroundColor: esActivo ? "#014e65" : "#cbd5e1",
                              },
                            }}
                          >
                            {numPag}
                          </IconButton>
                        );
                      })}

                      <IconButton
                        disabled={paginaDia >= diasConRegistros.length}
                        onClick={() => setPaginaDia((prev) => Math.min(diasConRegistros.length, prev + 1))}
                        sx={{ backgroundColor: "#e2e8f0", "&:hover": { backgroundColor: "#cbd5e1" } }}
                      >
                        <NavigateNextIcon />
                      </IconButton>

                      <Typography variant="body2" sx={{ ml: 2, color: "#64748b", fontWeight: "bold" }}>
                        Día: {diaActivo} ({paginaDia} de {diasConRegistros.length})
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Botones flotantes laterales derechos para exportar PDF y Excel */}
        <Box
          sx={{
            position: "fixed",
            right: "3vw",
            top: "35vh",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          {/* Botón PDF */}
          <Tooltip
            title="Exportar Reporte a PDF"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarPDF}
              sx={{
                backgroundColor: "#ffffff",
                color: "#d32f2f",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFilePdfBox} size={1.5} color="#d32f2f" />
            </IconButton>
          </Tooltip>

          {/* Botón Excel */}
          <Tooltip
            title="Exportar Reporte a Excel"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarExcel}
              sx={{
                backgroundColor: "#ffffff",
                color: "#2e7d32",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFileExcel} size={1.5} color="#2e7d32" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default SolicitudesGeneradas;
